/**
 * @file fileBase.ts
 * The shared basis for file input and file drop.
 *
 * AsFileHandle is the single file value type used across the file APIs
 * (`as-files-selected` from <as-file-input>, `as-files-dropped` from drop
 * zones). The rest is the drag-and-drop plumbing every drop zone shares:
 *
 *   FILES_DROPPED_EVENT     the one drop event name, for every zone
 *   DropSource              which zone a drop came from
 *   DroppedFilesDetail      detail carried by the drop event
 *   sendDroppedFiles        dispatch a drop from a zone
 *   isFileDragEvent         guard: does the DragEvent carry files?
 *   extractDroppedFiles     read AsFileHandle[] from a DragEvent
 *   DropOverlay             visual overlay injected into a shadow root
 *   DragDropHandler         attaches/removes the four drag event listeners
 *
 * Precedence contract
 * -------------------
 * Child drop zones (as-viewport, as-file-input) call e.stopPropagation() on
 * the native drop event inside their onDrop callback. Parent zones listening
 * in bubble phase (as-app file-droppable) therefore never see drops that a
 * child already handled.
 */

import { AS_FILE_EVENTS, sendEv } from '../../../data/events.ts';

// ---- AsFileHandle ----

/**
 * A selected file. Mirrors the native `File` surface (`name`, `type`, `size`,
 * `arrayBuffer()`, `text()`) so code written against `<input type=file>` works
 * unchanged, and adds `blob` / `toFile()` / `dataUrl()` for the cases File
 * doesn't cover. Immutable, no caching — each conversion reads the source.
 */
export class AsFileHandle {
  /** Like File.name — filename incl. extension, e.g. "part.stl". */
  readonly name: string;
  /** Like File.type — MIME, or '' when unknown (exactly as File reports it).
   *  For an extension, parse `name` (e.g. `name.split('.').pop()`). */
  readonly type: string;

  private readonly _source: File | Blob;

  constructor(source: File | Blob, name?: string, type?: string) {
    this._source = source;
    const isFile = source instanceof File;
    this.name = name ?? (isFile ? (source as File).name : '');
    this.type = type || source.type; // '' when unknown, same as File
  }

  /** Like File.size — bytes. */
  get size(): number { return this._source.size; }

  /** Lowercased file extension without the dot (e.g. "stl"), derived from `name`.
   *  `''` when the name has no extension. Use this instead of parsing `type`,
   *  which is the MIME type and is often empty for meshes. */
  get extension(): string {
    const dot = this.name.lastIndexOf('.');
    return dot > 0 ? this.name.slice(dot + 1).toLowerCase() : '';
  }

  /** The filename without its extension (e.g. "part" for "part.stl"), preserving
   *  case. The whole `name` when there is no extension. Complements `extension`. */
  get stem(): string {
    const dot = this.name.lastIndexOf('.');
    return dot > 0 ? this.name.slice(0, dot) : this.name;
  }

  /** Like File.arrayBuffer(). */
  arrayBuffer(): Promise<ArrayBuffer> { return this._source.arrayBuffer(); }

  /** Like File.text(). */
  text(): Promise<string> { return this._source.text(); }

  /** The raw Blob — for Blob-taking APIs (fetch body, FormData, configurator upload). */
  get blob(): Blob { return this._source; }

  /** A real File, always. Returns the original File, or synthesizes one from the
   *  Blob (host-provider / GrabCAD imports). Never null. */
  toFile(): File {
    return this._source instanceof File
      ? this._source
      : new File([this._source], this.name, { type: this.type || 'application/octet-stream' });
  }

  /** Convenience beyond the File API: a base64 data: URL. */
  dataUrl(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(this._source);
    });
  }

  // ---- Deprecated aliases (back-compat) -------------------------------------
  // Renamed to `name` / `type` to match the native File API. Kept so pinned
  // consumers (Outlines2, tutorial, shadow-board) don't break when they upgrade
  // the bundle.
  // TODO(file-api-cleanup): remove `filename` / `filetype` once all consumers
  // have migrated to `name` / `type`.

  /** @deprecated Use `name` (matches File.name). */
  get filename(): string { return this.name; }
  /** @deprecated Use `type` (matches File.type). */
  get filetype(): string { return this.type; }
}

// ---- Drop events ----

/** One event for every drop zone. `detail.source` says which zone it came from. */
export const FILES_DROPPED_EVENT = AS_FILE_EVENTS.DROPPED;

//TODO:@REMOVE
/** @deprecated listen for `FILES_DROPPED_EVENT` and check `detail.source === 'viewport'`.
 *  Still dispatched alongside it, with the same detail. */
export const DROPPED_FILES_EVENT = AS_FILE_EVENTS.DROPPED_VIEWPORT;
//TODO:@REMOVE
/** @deprecated listen for `FILES_DROPPED_EVENT` and check `detail.source === 'app'`.
 *  Still dispatched alongside it, with the same detail. */
export const APP_DROPPED_FILES_EVENT = AS_FILE_EVENTS.DROPPED_APP;

export const IMPORT_ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style="transform:scaleY(-1)"><path d="M224 30v256h-64l96 128 96-128h-64V30h-64zM32 434v48h448v-48H32z" fill="#000000" fill-opacity="1"/></svg>`;
export const TICK_SVG         = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M17.47 250.9C88.82 328.1 158 397.6 224.5 485.5c72.3-143.8 146.3-288.1 268.4-444.37L460 26.06C356.9 135.4 276.8 238.9 207.2 361.9c-48.4-43.6-126.62-105.3-174.38-137z" fill="#000000" fill-opacity="1"/></svg>`;

// ---- Types ----

/** The drop zone a file landed on. */
export type DropSource = 'viewport' | 'app';

/** Detail carried by `as-files-dropped`. */
export interface DroppedFilesDetail {
  files: AsFileHandle[];
  source: DropSource;
}

/**
 * @deprecated Drop events now carry `AsFileHandle[]` (unified with
 * <as-file-input>'s `as-files-selected`). This alias keeps old type imports
 * compiling.
 * TODO(file-api-cleanup): remove `DroppedFileInfo` once consumers import
 * `AsFileHandle` directly.
 */
export type DroppedFileInfo = AsFileHandle;

// ---- Utilities ----

/**
 * Announce a drop. Fires `as-files-dropped` on the zone element (bubbling and
 * composed, so window listeners see it) and the deprecated per-source event
 * alongside it. Both carry the same detail object, which is how consumers
 * listening for both names can tell they have one drop, not two.
 */
export function sendDroppedFiles(host: HTMLElement, source: DropSource, files: AsFileHandle[]): void {
  const detail: DroppedFilesDetail = { files, source };
  sendEv(host, FILES_DROPPED_EVENT, { detail });
  //TODO:@REMOVE the legacy aliases. The app one stays on window, where it has
  // always been dispatched.
  if (source === 'app') sendEv(window, APP_DROPPED_FILES_EVENT, { detail });
  else                  sendEv(host,   DROPPED_FILES_EVENT,     { detail });
}

export function isFileDragEvent(e: DragEvent): boolean {
  return !!e.dataTransfer?.types?.includes('Files');
}

/** Wrap each dropped native File in an AsFileHandle — the same value type
 *  <as-file-input> emits, so drop and pick consumers read one shape. */
export function extractDroppedFiles(e: DragEvent): AsFileHandle[] {
  const files = e.dataTransfer?.files;
  if (!files?.length) return [];
  return Array.from(files).map(f => new AsFileHandle(f));
}

export let dropOverlaySpinner = false;

// ---- DropOverlay ----

/**
 * Manages a purely visual drop-overlay element inside a shadow root.
 * The host's stylesheet must define styles for .drop-overlay and .drop-overlay__icon.
 */
export class DropOverlay {
  private _el:   HTMLElement;
  private _icon: HTMLElement;
  private _postDrop = false;

  constructor(shadowRoot: ShadowRoot) {
    this._el = document.createElement('div');
    this._el.className = 'drop-overlay';
    this._icon = document.createElement('div');
    this._icon.className = 'drop-overlay__icon';
    this._icon.innerHTML = IMPORT_ARROW_SVG;
    this._el.appendChild(this._icon);
    shadowRoot.appendChild(this._el);
  }

  /** True while the post-drop animation is running — suppresses new enter/leave reactions. */
  get postDrop(): boolean { return this._postDrop; }

  show(): void { this._el.classList.add('active'); }
  hide(): void { this._el.classList.remove('active'); }

  setIcon(state: 'arrow' | 'spinner' | 'tick'): void {
    switch (state) {
      case 'arrow':   this._icon.innerHTML = IMPORT_ARROW_SVG; break;
      case 'spinner': this._icon.innerHTML = '<as-spinner></as-spinner>'; break;
      case 'tick':    this._icon.innerHTML = TICK_SVG; break;
    }
  }

  /** Remove the overlay element from the shadow root (call when disabling the drop zone). */
  remove(): void {
    this._el.remove();
  }

  /** Runs the spinner → tick → hide sequence after a successful drop. */
  animateDrop(): void {
    this._postDrop = true;
    if (dropOverlaySpinner) {
      this.setIcon('spinner');
      setTimeout(() => {
        this.setIcon('tick');
        setTimeout(() => {
          this._postDrop = false;
          this.hide();
          this.setIcon('arrow');
        }, 800);
      }, 500);
    } else {
      this.setIcon('tick');
      setTimeout(() => {
        this._postDrop = false;
        this.hide();
        this.setIcon('arrow');
      }, 800);
    }
  }
}

// ---- DragDropHandler ----

/**
 * Attaches dragenter/dragleave/dragover/drop listeners to a host element,
 * optionally driving a DropOverlay for visual feedback.
 *
 * @param host     Element to attach listeners to.
 * @param onDrop   Called with the extracted files and the original DragEvent.
 *                 Call e.stopPropagation() inside to prevent parent drop zones
 *                 (e.g. as-app file-droppable) from also handling the same drop.
 * @param overlay  Optional DropOverlay to show/animate. Pass null for zones
 *                 that need no visual feedback (e.g. app-level fallback handler).
 * @param capture  Whether to attach in capture phase (default true).
 *                 Use true for child zones so they intercept before parents.
 *                 Use false for app-level fallback listeners (bubble phase).
 */
export class DragDropHandler {
  private _host:     HTMLElement;
  private _overlay:  DropOverlay | null;
  private _onDropCb: (files: AsFileHandle[], e: DragEvent) => void;
  private _capture:  boolean;

  constructor(
    host: HTMLElement,
    onDrop: (files: AsFileHandle[], e: DragEvent) => void,
    overlay: DropOverlay | null = null,
    capture = true,
  ) {
    this._host     = host;
    this._onDropCb = onDrop;
    this._overlay  = overlay;
    this._capture  = capture;

    host.addEventListener('dragenter', this._onDragEnter, capture);
    host.addEventListener('dragleave', this._onDragLeave, capture);
    host.addEventListener('dragover',  this._onDragOver,  capture);
    host.addEventListener('drop',      this._onDrop,      capture);
  }

  private _onDragEnter = (e: DragEvent): void => {
    if (this._overlay?.postDrop) return;
    if (!isFileDragEvent(e)) return;
    this._overlay?.show();
  };

  private _onDragLeave = (e: DragEvent): void => {
    if (this._overlay?.postDrop) return;
    const related = e.relatedTarget as Node | null;
    const host    = this._host;
    if (!related || (!host.contains(related) && !host.shadowRoot?.contains(related))) {
      this._overlay?.hide();
    }
  };

  private _onDragOver = (e: DragEvent): void => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
  };

  private _onDrop = (e: DragEvent): void => {
    e.preventDefault();
    const files = extractDroppedFiles(e);
    if (!files.length) {
      this._overlay?.hide();
      return;
    }
    this._overlay?.animateDrop();
    this._onDropCb(files, e);
  };

  destroy(): void {
    this._host.removeEventListener('dragenter', this._onDragEnter, this._capture);
    this._host.removeEventListener('dragleave', this._onDragLeave, this._capture);
    this._host.removeEventListener('dragover',  this._onDragOver,  this._capture);
    this._host.removeEventListener('drop',      this._onDrop,      this._capture);
  }
}
